<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  onUnmounted,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import QRCode from 'qrcode';

import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LineChart from 'dashboard/components-next/Charts/LineChart.vue';
import BarChart from 'dashboard/components-next/Charts/BarChart.vue';
import { useAlert } from 'dashboard/composables';
import { useDevicePairing } from 'dashboard/composables/useDevicePairing';
import callDevicesAPI from 'dashboard/api/callDevices';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// ── ids ────────────────────────────────────────────────────────────────────────
const deviceId = computed(() => Number(route.params.deviceId));

// ── remote state ───────────────────────────────────────────────────────────────
const device = ref(null);
const stats = ref(null);
const plan = ref(null);
const isFetching = ref(false);
const fetchError = ref(false);

// ── UI state ───────────────────────────────────────────────────────────────────
const editingName = ref(false);
const nameInput = ref('');
const sessionIdRevealed = ref(false);
const sessionIdCopied = ref(false);
const loadingAction = ref(null); // 'reconnect' | 'disconnect' | 'delete' | 'rename'

// ── data fetching ──────────────────────────────────────────────────────────────

const fetchAll = async () => {
  isFetching.value = true;
  fetchError.value = false;
  try {
    const [deviceRes, statsRes, planRes] = await Promise.all([
      callDevicesAPI.show(deviceId.value),
      callDevicesAPI.stats(deviceId.value),
      callDevicesAPI.callingPlan(),
    ]);
    device.value = deviceRes.data;
    stats.value = statsRes.data;
    plan.value = planRes.data;
    nameInput.value = device.value.name ?? '';
  } catch {
    fetchError.value = true;
  } finally {
    isFetching.value = false;
  }
};

onBeforeMount(fetchAll);

// ── status ─────────────────────────────────────────────────────────────────────

const deviceStatus = computed(() => {
  if (!device.value) return 'disconnected';
  if (device.value.state === 'open' && device.value.paired) return 'connected';
  if (device.value.state === 'connecting' || device.value.state === 'qr')
    return 'pairing';
  return 'disconnected';
});

const statusLabel = computed(() => {
  if (deviceStatus.value === 'connected') {
    const number = device.value?.jid?.split('@')[0] ?? '';
    return t('CALL_DEVICES.DASHBOARD.CONNECTED_WITH', { number: `+${number}` });
  }
  if (deviceStatus.value === 'pairing')
    return t('CALL_DEVICES.DASHBOARD.PAIRING');
  return t('CALL_DEVICES.DASHBOARD.DISCONNECTED');
});

// ── session_id mask/reveal/copy ────────────────────────────────────────────────

const maskedSessionId = computed(() => {
  const sid = device.value?.session_id ?? '';
  if (!sid) return '—';
  if (sessionIdRevealed.value) return sid;
  if (sid.length <= 8) return '•'.repeat(sid.length);
  return `${sid.slice(0, 4)}${'•'.repeat(sid.length - 8)}${sid.slice(-4)}`;
});

const copySessionId = async () => {
  try {
    await navigator.clipboard.writeText(device.value?.session_id ?? '');
    sessionIdCopied.value = true;
    setTimeout(() => {
      sessionIdCopied.value = false;
    }, 2000);
  } catch {
    // clipboard unavailable in test/HTTP environments — silently ignore
  }
};

// ── KPI formatting ─────────────────────────────────────────────────────────────

const formattedAnswerRate = computed(() => {
  if (!stats.value) return '—';
  return `${Math.round((stats.value.answer_rate ?? 0) * 100)}%`;
});

const formattedAvgDuration = computed(() => {
  if (!stats.value) return '—';
  const secs = Math.round(stats.value.avg_duration_seconds ?? 0);
  if (secs === 0) return '—';
  if (secs < 60) return t('CALL_DEVICES.DASHBOARD.KPI.SECS', { s: secs });
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (s === 0) return t('CALL_DEVICES.DASHBOARD.KPI.MINS', { m });
  return t('CALL_DEVICES.DASHBOARD.KPI.MIN_SEC', { m, s });
});

// ── chart data ─────────────────────────────────────────────────────────────────

const volumeChartData = computed(() => {
  if (!stats.value?.volume_30d?.length) return { labels: [], datasets: [] };

  const labels = stats.value.volume_30d.map(d => {
    // "YYYY-MM-DD" → "DD/MM"
    const parts = d.date.split('-');
    return `${parts[2]}/${parts[1]}`;
  });

  return {
    labels,
    datasets: [
      {
        label: t('CALL_DEVICES.DASHBOARD.CHARTS.INBOUND'),
        data: stats.value.volume_30d.map(d => d.inbound),
        borderColor: '#22d3ee',
        backgroundColor: 'rgba(34,211,238,0.10)',
        fill: true,
      },
      {
        label: t('CALL_DEVICES.DASHBOARD.CHARTS.OUTBOUND'),
        data: stats.value.volume_30d.map(d => d.outbound),
        borderColor: '#818cf8',
        backgroundColor: 'rgba(129,140,248,0.08)',
        fill: true,
      },
    ],
  };
});

const peakChartData = computed(() => {
  if (!stats.value?.peak_hours?.length) return { labels: [], datasets: [] };

  return {
    labels: stats.value.peak_hours.map(h => `${h.hour}h`),
    datasets: [
      {
        label: t('CALL_DEVICES.DASHBOARD.CHARTS.CALLS'),
        data: stats.value.peak_hours.map(h => h.count),
        backgroundColor: 'rgba(129,140,248,0.55)',
        hoverBackgroundColor: 'rgba(129,140,248,0.85)',
      },
    ],
  };
});

// Expose legend colors via a simple ref for custom HTML legend
const legendItems = computed(() => [
  { label: t('CALL_DEVICES.DASHBOARD.CHARTS.INBOUND'), color: '#22d3ee' },
  { label: t('CALL_DEVICES.DASHBOARD.CHARTS.OUTBOUND'), color: '#818cf8' },
]);

// ── actions ────────────────────────────────────────────────────────────────────

const goBack = () => {
  router.push({ name: 'call_devices_list', params: route.params });
};

// ── reconnect (QR re-pair) ───────────────────────────────────────────────────
const pairDialogRef = ref(null);
const pairCanvasRef = ref(null);
const {
  qr: pairQr,
  paired: pairPaired,
  start: startPairing,
  stop: stopPairing,
} = useDevicePairing();

const renderPairQr = async qrString => {
  if (!qrString || !pairCanvasRef.value) return;
  try {
    await QRCode.toCanvas(pairCanvasRef.value, qrString, {
      width: 240,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch {
    // ignore render errors — next poll will retry
  }
};

// Render the QR whenever the polling loop surfaces a new one.
watch(pairQr, async value => {
  if (!value) return;
  await nextTick();
  renderPairQr(value);
});

// Re-render once the canvas mounts (dialog opened after QR already arrived).
watch(pairCanvasRef, canvas => {
  if (canvas && pairQr.value) renderPairQr(pairQr.value);
});

// Close the dialog and refresh once the device reconnects.
watch(pairPaired, async value => {
  if (!value) return;
  useAlert(t('CALL_DEVICES.TOAST.PAIR_SUCCESS'));
  setTimeout(() => pairDialogRef.value?.close(), 1500);
  await fetchAll();
});

const handleReconnect = async () => {
  loadingAction.value = 'reconnect';
  try {
    pairDialogRef.value?.open();
    await startPairing(device.value.id, { repair: true });
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.PAIR_ERROR'));
  } finally {
    loadingAction.value = null;
  }
};

const handlePairDialogClose = () => {
  stopPairing();
};

onUnmounted(stopPairing);

const handleDisconnect = async () => {
  loadingAction.value = 'disconnect';
  try {
    await callDevicesAPI.logout(device.value.id);
    useAlert(t('CALL_DEVICES.TOAST.LOGOUT_SUCCESS'));
    await fetchAll();
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.LOGOUT_ERROR'));
  } finally {
    loadingAction.value = null;
  }
};

const handleDelete = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CALL_DEVICES.DASHBOARD.DELETE_CONFIRM'))) return;
  loadingAction.value = 'delete';
  try {
    await callDevicesAPI.destroy(device.value.id);
    useAlert(t('CALL_DEVICES.TOAST.DELETE_SUCCESS'));
    router.push({ name: 'call_devices_list', params: route.params });
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.DELETE_ERROR'));
    loadingAction.value = null;
  }
};

const handleUpgrade = () => {
  // Calling is billed as a channel (existing subscription). Send to billing.
  try {
    router.push({ name: 'billing_settings_index' });
  } catch {
    // route unavailable — ignore rather than crash
  }
};

const startNameEdit = () => {
  nameInput.value = device.value?.name ?? '';
  editingName.value = true;
};

const cancelNameEdit = () => {
  editingName.value = false;
};

const saveName = async () => {
  if (!nameInput.value.trim()) {
    editingName.value = false;
    return;
  }
  loadingAction.value = 'rename';
  try {
    const { data } = await callDevicesAPI.update(device.value.id, {
      name: nameInput.value.trim(),
    });
    device.value = { ...device.value, ...data };
    useAlert(t('CALL_DEVICES.DASHBOARD.NAME_EDIT.SAVE_SUCCESS'));
  } catch {
    useAlert(t('CALL_DEVICES.DASHBOARD.NAME_EDIT.SAVE_ERROR'));
  } finally {
    editingName.value = false;
    loadingAction.value = null;
  }
};

const onNameKeydown = e => {
  if (e.key === 'Enter') saveName();
  if (e.key === 'Escape') cancelNameEdit();
};
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching && !device"
    :loading-message="$t('CALL_DEVICES.DASHBOARD.LOADING')"
    :no-records-found="!isFetching && fetchError"
    :no-records-message="$t('CALL_DEVICES.DASHBOARD.ERROR')"
  >
    <template #header>
      <!-- Back navigation -->
      <div class="flex items-center gap-2 -mb-2">
        <button
          type="button"
          class="flex items-center gap-1.5 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
          {{ $t('CALL_DEVICES.DASHBOARD.BACK') }}
        </button>
      </div>
    </template>

    <template #body>
      <div v-if="device" class="flex flex-col gap-6">
        <!-- ── Device card ─────────────────────────────────────────────────── -->
        <div
          class="rounded-2xl border border-n-weak bg-n-alpha-1 p-5 flex flex-col gap-4"
        >
          <!-- top row: name + actions -->
          <div class="flex flex-wrap items-start justify-between gap-3">
            <!-- Name / inline edit -->
            <div class="flex items-center gap-2 min-w-0">
              <span
                class="i-lucide-smartphone size-5 text-n-brand flex-shrink-0"
              />
              <template v-if="editingName">
                <input
                  v-model="nameInput"
                  class="h-8 px-2 text-base font-semibold rounded-lg bg-n-alpha-2 border-0 outline outline-1 outline-n-brand text-n-slate-12 w-56"
                  autofocus
                  @keydown="onNameKeydown"
                  @blur="saveName"
                />
              </template>
              <template v-else>
                <span class="text-base font-semibold text-n-slate-12 truncate">
                  {{ device.name }}
                </span>
                <button
                  type="button"
                  class="i-lucide-pencil size-3.5 text-n-slate-9 hover:text-n-slate-12 transition-colors flex-shrink-0"
                  :aria-label="$t('CALL_DEVICES.DASHBOARD.NAME_EDIT.EDIT')"
                  @click="startNameEdit"
                />
              </template>
            </div>

            <!-- Action buttons -->
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <Button
                v-tooltip.top="$t('CALL_DEVICES.DASHBOARD.ACTIONS.RECONNECT')"
                icon="i-lucide-qr-code"
                xs
                slate
                faded
                :is-loading="loadingAction === 'reconnect'"
                @click="handleReconnect"
              />
              <Button
                v-if="deviceStatus === 'connected'"
                v-tooltip.top="$t('CALL_DEVICES.DASHBOARD.ACTIONS.DISCONNECT')"
                icon="i-lucide-log-out"
                xs
                amber
                faded
                :is-loading="loadingAction === 'disconnect'"
                @click="handleDisconnect"
              />
              <Button
                v-tooltip.top="$t('CALL_DEVICES.DASHBOARD.ACTIONS.DELETE')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loadingAction === 'delete'"
                @click="handleDelete"
              />
            </div>
          </div>

          <!-- session_id row -->
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.SESSION_ID.LABEL') }}:
            </span>
            <code
              class="text-xs font-mono text-n-slate-11 bg-n-alpha-2 px-2 py-0.5 rounded select-all"
            >
              {{ maskedSessionId }}
            </code>
            <button
              type="button"
              class="text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click="sessionIdRevealed = !sessionIdRevealed"
            >
              {{
                sessionIdRevealed
                  ? $t('CALL_DEVICES.DASHBOARD.SESSION_ID.HIDE')
                  : $t('CALL_DEVICES.DASHBOARD.SESSION_ID.REVEAL')
              }}
            </button>
            <button
              type="button"
              class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click="copySessionId"
            >
              <span
                :class="
                  sessionIdCopied
                    ? 'i-lucide-check text-n-teal-9'
                    : 'i-lucide-copy'
                "
                class="size-3.5"
              />
              {{
                sessionIdCopied
                  ? $t('CALL_DEVICES.DASHBOARD.SESSION_ID.COPIED')
                  : $t('CALL_DEVICES.DASHBOARD.SESSION_ID.COPY')
              }}
            </button>
          </div>

          <!-- status + badges row -->
          <div class="flex flex-wrap items-center justify-between gap-3">
            <!-- Status -->
            <div class="flex items-center gap-2">
              <template v-if="deviceStatus === 'connected'">
                <span class="w-2 h-2 rounded-full bg-n-teal-9 flex-shrink-0" />
                <span class="text-sm text-n-teal-9 font-medium">
                  {{ statusLabel }}
                </span>
              </template>
              <template v-else-if="deviceStatus === 'pairing'">
                <span
                  class="i-lucide-loader-2 size-3.5 text-n-amber-9 animate-spin flex-shrink-0"
                />
                <span class="text-sm text-n-amber-9 font-medium">
                  {{ statusLabel }}
                </span>
              </template>
              <template v-else>
                <span class="w-2 h-2 rounded-full bg-n-slate-7 flex-shrink-0" />
                <span class="text-sm text-n-slate-10">{{ statusLabel }}</span>
                <Button
                  :label="$t('CALL_DEVICES.DASHBOARD.ACTIONS.RECONNECT')"
                  icon="i-lucide-qr-code"
                  xs
                  teal
                  class="ml-1"
                  :is-loading="loadingAction === 'reconnect'"
                  @click="handleReconnect"
                />
              </template>
            </div>

            <!-- Badges + Upgrade -->
            <div class="flex items-center gap-2">
              <span
                class="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11 border border-n-amber-6"
              >
                {{ $t('CALL_DEVICES.DASHBOARD.BADGE_UNOFFICIAL') }}
              </span>
              <span
                v-if="plan"
                class="text-xs px-2.5 py-0.5 rounded-full bg-n-brand/10 text-n-brand border border-n-brand/20 font-medium"
              >
                {{ plan.plan_name }}
              </span>
              <button
                type="button"
                class="flex items-center gap-1 text-xs text-n-brand hover:underline font-medium ml-1"
                @click="handleUpgrade"
              >
                <span class="i-lucide-zap size-3" />
                {{ $t('CALL_DEVICES.DASHBOARD.ACTIONS.UPGRADE') }}
              </button>
            </div>
          </div>
        </div>

        <!-- ── KPI cards ───────────────────────────────────────────────────── -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <!-- Canais -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.CHANNELS') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ plan ? plan.channels_in_use : '—' }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{
                $t('CALL_DEVICES.DASHBOARD.KPI.OF_MAX', {
                  max: plan?.max_channels ?? '—',
                })
              }}
            </span>
          </div>

          <!-- Uso -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.USAGE') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ plan ? plan.devices_used : '—' }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{
                $t('CALL_DEVICES.DASHBOARD.KPI.OF_MAX', {
                  max: plan?.max_devices ?? '—',
                })
              }}
            </span>
          </div>

          <!-- Total de chamadas -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.TOTAL_CALLS') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ stats ? stats.total : '—' }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.LAST_30D') }}
            </span>
          </div>

          <!-- Taxa de atendimento -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.ANSWER_RATE') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ formattedAnswerRate }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.LAST_30D') }}
            </span>
          </div>

          <!-- Duração média -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.AVG_DURATION') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ formattedAvgDuration }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.LAST_30D') }}
            </span>
          </div>

          <!-- Chamadas perdidas -->
          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-10 font-medium">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.MISSED') }}
            </span>
            <span
              class="text-2xl font-semibold tabular-nums"
              :class="
                stats && stats.missed > 0 ? 'text-n-ruby-10' : 'text-n-slate-12'
              "
            >
              {{ stats ? stats.missed : '—' }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ $t('CALL_DEVICES.DASHBOARD.KPI.LAST_30D') }}
            </span>
          </div>
        </div>

        <!-- ── Charts ──────────────────────────────────────────────────────── -->
        <div class="flex flex-col gap-6">
          <!-- Volume de chamadas -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-5">
            <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ $t('CALL_DEVICES.DASHBOARD.CHARTS.VOLUME_TITLE') }}
              </span>
              <!-- Custom HTML legend -->
              <div class="flex items-center gap-4">
                <div
                  v-for="item in legendItems"
                  :key="item.label"
                  class="flex items-center gap-1.5"
                >
                  <span
                    class="w-4 h-0.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: item.color }"
                  />
                  <span class="text-xs text-n-slate-10">{{ item.label }}</span>
                </div>
              </div>
            </div>
            <LineChart
              v-if="volumeChartData.labels.length"
              :data="volumeChartData"
              height="200px"
            />
            <div
              v-else
              class="h-[200px] flex items-center justify-center text-sm text-n-slate-9"
            >
              {{ $t('CALL_DEVICES.DASHBOARD.CHARTS.NO_DATA') }}
            </div>
          </div>

          <!-- Horários de pico -->
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-5">
            <span class="text-sm font-semibold text-n-slate-12 block mb-4">
              {{ $t('CALL_DEVICES.DASHBOARD.CHARTS.PEAK_TITLE') }}
            </span>
            <BarChart
              v-if="peakChartData.labels.length"
              :data="peakChartData"
              height="180px"
            />
            <div
              v-else
              class="h-[180px] flex items-center justify-center text-sm text-n-slate-9"
            >
              {{ $t('CALL_DEVICES.DASHBOARD.CHARTS.NO_DATA') }}
            </div>
          </div>
        </div>
      </div>

      <!-- ── Reconnect (QR re-pair) dialog ─────────────────────────────────── -->
      <Dialog
        ref="pairDialogRef"
        :title="$t('CALL_DEVICES.DASHBOARD.RECONNECT_MODAL.TITLE')"
        :show-confirm-button="false"
        :show-cancel-button="false"
        width="md"
        @close="handlePairDialogClose"
      >
        <div v-if="pairPaired" class="flex flex-col items-center gap-4 py-6">
          <span class="i-lucide-circle-check-big size-14 text-n-teal-9" />
          <p class="text-base font-medium text-n-slate-12 text-center">
            {{ $t('CALL_DEVICES.DASHBOARD.RECONNECT_MODAL.CONNECTED') }}
          </p>
        </div>
        <div v-else class="flex flex-col items-center gap-4 py-2">
          <div
            v-if="!pairQr"
            class="flex items-center justify-center w-60 h-60 rounded-xl bg-n-alpha-1"
          >
            <div class="flex flex-col items-center gap-2 text-n-slate-10">
              <span class="i-lucide-loader-2 animate-spin size-8" />
              <span class="text-sm">
                {{ $t('CALL_DEVICES.DASHBOARD.RECONNECT_MODAL.WAITING_QR') }}
              </span>
            </div>
          </div>
          <canvas
            v-show="pairQr"
            ref="pairCanvasRef"
            class="rounded-xl overflow-hidden"
          />
          <div class="text-center space-y-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('CALL_DEVICES.DASHBOARD.RECONNECT_MODAL.SCANNING') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ $t('CALL_DEVICES.DASHBOARD.RECONNECT_MODAL.QR_ROTATE') }}
            </p>
          </div>
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
